import clsx from "clsx";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import EditButtonIcon from "@mui/icons-material/EditOutlined";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { makeStyles } from "@mui/styles";
import { createAddressFieldPath } from "./helpers/addressUtils";

const useStyles = makeStyles((theme) => ({
  table: {
    "& tbody tr:nth-of-type(odd)": {
      backgroundColor: "rgba(0,0,0,.03)",
    },
    borderRadius: 6,
    overflow: "hidden",
    "@media print": {
      tableLayout: "fixed",
      width: "100%",
      display: "table",
    },
  },
  th: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    borderBottom: "none",
    color: theme.palette.text.secondary,
  },
  labelCell: {
    width: 140,
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.primary,
    whiteSpace: "normal",
    "& .titlePart": {
      whiteSpace: "nowrap",
    },
  },
  td: {
    fontSize: 12,
    "@media print": {
      wordWrap: "break-word",
    },
  },
  empty: {
    opacity: 0.35,
    fontStyle: "italic",
  },
  printOnly: {
    display: "none",
    "@media print": { display: "table" },
  },
  // --- editable UX ---
  editableCell: {
    position: "relative",
    paddingRight: 34, // plats för penna
    cursor: "text",
    "&:hover $editBtn": {
      opacity: 1,
      transform: "translateY(-50%) scale(1)",
    },
  },
  editBtn: {
    position: "absolute",
    right: 6,
    top: "50%",
    transform: "translateY(-50%) scale(0.95)",
    opacity: 0,
    transition: "opacity 120ms ease, transform 120ms ease",
    padding: 4,
  },
  inputRoot: {
    fontSize: 12,
  },
  inputBase: {
    fontSize: 12,
    padding: "2px 0",
  },
  dirtyCell: {
    backgroundColor: "rgba(255, 193, 7, 0.12)", // mjuk gul ton
    boxShadow: "inset 3px 0 0 #f7bd02", // accent på vänsterkant (din färg!)
    transition: "background-color 150ms ease",
  },
  editingCell: {
    paddingTop: 5,
    paddingBottom: 0,
  },
}));

const FIELD_ORDER = [
  "address1",
  "address2",
  "address3",
  "address4",
  "address5",
  "city",
  "zip",
  "country",
  "reference",
  "email",
  "telephone",
  "contact",
];

const normalizeAddress = (addr = {}) => {
  const clean = {};
  Object.entries(addr).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (key.startsWith("__")) return;
    if (key.startsWith("orderHistory.")) {
      clean[key.replace("orderHistory.", "")] = value;
    } else {
      clean[key] = value;
    }
  });
  return clean;
};

const AddressCompareTable = ({
  columns,
  title,
  className,
  printOnly,
  editable,
  isFieldDirty,
  onChangeCell,
}) => {
  const classes = useStyles();

  const normalizedColumns = useMemo(
    () =>
      columns.map((c) => ({
        ...c,
        address: normalizeAddress(c.address),
      })),
    [columns]
  );

  const tableRows = useMemo(
    () => {
      const visibleKeys = FIELD_ORDER.filter((key) =>
        normalizedColumns.some((c) => c.address?.[key])
      );

      const keysToRender = visibleKeys.length
        ? visibleKeys
        : editable
          ? FIELD_ORDER
          : [];

      return keysToRender.map((key) => {
        const distinctTitles = new Set(normalizedColumns.map(col => col.getAddressLabel(key)));
        return {
          key,
          titles: Array.from(distinctTitles),
        };
      });
    },
    [normalizedColumns, editable]
  );

  const [editing, setEditing] = useState(null); 
  // editing = { colId, key, draft, original }

  const isEditing = (colId, key) =>
    editing?.colId === colId && editing?.key === key;

  const startEdit = (colId, key, currentValue) => {
    if (!editable || printOnly) return;
    setEditing({
      colId,
      key,
      original: currentValue ?? "",
      draft: currentValue ?? "",
    });
  };

  const cancelEdit = () => {
    setEditing(null);
  };

  const commitEdit = () => {
    if (!editing) return;
    const { colId, key, draft, original } = editing;
    const next = (draft ?? "").trim();
    const prev = (original ?? "").trim();

    if (onChangeCell && next !== prev) {
      onChangeCell(colId, key, next);
    }

    setEditing(null);
  };

  if (!tableRows.length) return null;

  return (
    <Table
      className={clsx(
        classes.table,
        printOnly && classes.printOnly,
        className
      )}
      size="small"
    >
      <TableHead>
        <TableRow>
          <TableCell className={classes.th}>
            <Typography className={classes.th}>{title}</Typography>
          </TableCell>
          {normalizedColumns.map((c) => (
            <TableCell key={c.title} className={classes.th}>
              {c.title}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {tableRows.map((row) => (
          <TableRow key={row.key}>
            <TableCell className={`${classes.td} ${classes.labelCell}`} sx={{ textTransform: "capitalize" }}>
              {row.titles.map(title => <Box className={classes.titlePart} >{title}</Box>)}
            </TableCell>

            {normalizedColumns.map((c) => {
              const colId = c.id;
              const val = c.address?.[row.key];
              const editableHere =
                editable && !printOnly && typeof onChangeCell === "function" && !!colId;

              const addressFieldPath = createAddressFieldPath(colId, row.key);
              const isDirtyCell = isFieldDirty?.(addressFieldPath);
              const isEditingCell = isEditing(colId, row.key);

              return (
                <TableCell
                  key={`${c.id}-${row.key}`}
                  className={clsx(
                    classes.td,
                    editableHere && classes.editableCell,
                    isDirtyCell && classes.dirtyCell,
                    isEditingCell && classes.editingCell
                  )}
                  onClick={() => editableHere && startEdit(colId, row.key, val)}
                >
                  {editableHere && (
                    <Tooltip title="Redigera">
                      <IconButton
                        className={classes.editBtn}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(colId, row.key, val);
                        }}
                      >
                        <EditButtonIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {editableHere && isEditingCell ? (
                    <TextField
                      autoFocus
                      fullWidth
                      variant="standard"
                      value={editing.draft}
                      onChange={(e) =>
                        setEditing((prev) => ({ ...prev, draft: e.target.value }))
                      }
                      onBlur={commitEdit}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitEdit();
                        }
                        if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                      InputProps={{
                        classes: { root: classes.inputRoot, input: classes.inputBase },
                        disableUnderline: false,
                      }}
                    />
                  ) : (
                    val ? val : <span className={classes.empty}>—</span>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

AddressCompareTable.propTypes = {
  title: PropTypes.string.isRequired,
  className: PropTypes.string,
  printOnly: PropTypes.bool,
  editable: PropTypes.bool,
  isFieldDirty: PropTypes.func,
  onChangeCell: PropTypes.func,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string, // "delivery" | "invoice" | "orderer"
      title: PropTypes.string.isRequired,
      address: PropTypes.object,
    })
  ).isRequired,
};

AddressCompareTable.defaultProps = {
  className: "",
  printOnly: false,
  editable: false,
  isFieldDirty: null,
  onChangeCell: null,
};

AddressCompareTable.displayName = "AddressCompareTable";

export default AddressCompareTable;
